import { useState } from 'react';
import type { FC } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowUpRight, Menu, Moon, Sun, X } from 'lucide-react';

const menuItems = [
  { name: 'Work', href: '#portfolio', id: 'work' },
  { name: 'Services', href: '#services', id: 'services' },
  { name: 'Approach', href: '#approach', id: 'approach' },
  { name: 'Contact', href: '#contact', id: 'contact' },
];

interface HeaderProps {
  navigate?: (path: string) => void;
  isAuthenticated?: boolean;
  theme?: 'light' | 'dark';
  onThemeChange?: (theme: 'light' | 'dark') => void;
  userInfo?: { name?: string; email?: string };
  logout?: () => Promise<void>;
  homeHref?: string;
  sectionHrefPrefix?: string;
}

const Header: FC<HeaderProps> = ({
  theme = 'dark',
  onThemeChange,
  homeHref = '#top',
  sectionHrefPrefix = '',
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const shouldReduceMotion = Boolean(useReducedMotion());
  const nextTheme = theme === 'dark' ? 'light' : 'dark';

  const renderThemeToggle = (location: 'desktop' | 'mobile') => (
    <button
      type="button"
      onClick={() => onThemeChange?.(nextTheme)}
      className="group flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-line/15 text-muted transition-all hover:border-accent/60 hover:text-accent"
      aria-label={`Switch to ${nextTheme} mode`}
      title={`Switch to ${nextTheme} mode`}
      data-agent-id={`agent://consltr/site/theme-toggle/${location}`}
      data-semantic-intent="toggle"
    >
      <motion.span
        key={theme}
        initial={shouldReduceMotion ? false : { opacity: 0, rotate: -45, scale: 0.7 }}
        animate={{ opacity: 1, rotate: 0, scale: 1 }}
        aria-hidden="true"
      >
        {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
      </motion.span>
    </button>
  );

  return (
    <motion.header
      initial={shouldReduceMotion ? false : { y: -80 }}
      animate={{ y: 0 }}
      className="fixed inset-x-0 top-0 z-50 border-b border-line/10 bg-canvas/85 backdrop-blur-xl transition-colors duration-300"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-8">
        <a
          href={homeHref}
          className="serif text-2xl font-semibold tracking-tight text-ink"
          data-agent-id="agent://consltr/site/nav/home"
          data-semantic-intent="navigate"
        >
          Consltr<span className="text-accent">.</span>
        </a>

        <nav aria-label="Primary navigation" className="hidden items-center gap-8 md:flex">
          {menuItems.map((item, index) => (
            <motion.a
              key={item.name}
              href={`${sectionHrefPrefix}${item.href}`}
              data-agent-id={`agent://consltr/site/nav/${item.id}`}
              data-semantic-intent="navigate"
              initial={shouldReduceMotion ? false : { opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.06 }}
              className="nav-motion-link text-sm font-medium text-muted transition-colors hover:text-ink"
            >
              {item.name}
            </motion.a>
          ))}
          <a
            href={`${sectionHrefPrefix}#contact`}
            data-agent-id="agent://consltr/site/nav/start-conversation"
            data-semantic-intent="navigate"
            className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-canvas transition-colors hover:bg-highlight hover:text-black"
          >
            Start a conversation
            <ArrowUpRight size={15} aria-hidden="true" />
          </a>
          {renderThemeToggle('desktop')}
        </nav>

        <div className="flex items-center gap-2 md:hidden">
          {renderThemeToggle('mobile')}
          <button
            type="button"
            onClick={() => setIsMenuOpen(open => !open)}
            className="rounded-full border border-line/15 p-2.5 text-ink"
            aria-expanded={isMenuOpen}
            aria-controls="mobile-navigation"
            aria-label={isMenuOpen ? 'Close navigation' : 'Open navigation'}
            data-agent-id="agent://consltr/site/nav/mobile-toggle"
            data-semantic-intent="toggle"
          >
            {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {isMenuOpen && (
        <motion.nav
          id="mobile-navigation"
          initial={shouldReduceMotion ? false : { opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          aria-label="Mobile navigation"
          className="border-t border-line/10 bg-canvas px-6 py-6 md:hidden"
        >
          <div className="mx-auto flex max-w-7xl flex-col gap-5">
            {menuItems.map(item => (
              <a
                key={item.name}
                href={`${sectionHrefPrefix}${item.href}`}
                onClick={() => setIsMenuOpen(false)}
                className="text-lg font-medium text-muted"
              >
                {item.name}
              </a>
            ))}
          </div>
        </motion.nav>
      )}
    </motion.header>
  );
};

export default Header;
